import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"

import Input from "@/components/core/Input/Input"
import Button from "@/components/core/Button/Button"
import ToolTip from "@/components/core/ToolTip/ToolTip"
import PasswordRequirements from "@/components/AccountSettings/v1/ResetPassword/PasswordRequirements"

import { resetPassword } from "@/store/features/genericSlice"
import { selectAuthState } from "@/store/features/authSlice"

import { sanitizeInnerHtml, getPasswordRegExp } from "@/utils/helper"
import { getManagePasswordSubmitAnalytics } from "@/components/AccountSettings/v1/ResetPassword/analytics"

import resetPasswordStyle from "@/components/AccountSettings/v1/ResetPassword/index.module.scss"

const ResetPassword = ({
  showModal,
  i18n = {},
  onSuccess = () => {},
  ...props
}) => {
  const dispatch = useDispatch()

  const { user } = useSelector(selectAuthState)
  const [showRequirements, setShowRequirements] = useState(false)
  const [data, setData] = useState({
    oldPassword: { value: "", error: false, mssg: "" },
    newPassword: { value: "", error: false, mssg: "" },
    rePassword: { value: "", error: false, mssg: "" },
  })
  const [passwordRegExp, setPasswordRegExp] = useState({
    regex: "",
    passwordMessage: [],
  })
 
  useEffect(() => {
    const handleClick = e => {
      if (e.target.attributes["name"]?.value !== "showModal")
        setShowRequirements(false)
    }

    getPasswordRegExp()?.then(getPasswordRegEx => {
      setPasswordRegExp({
        regex: getPasswordRegEx.regex ?? "",
        passwordMessage: getPasswordRegEx.passwordMessage ?? [],
      })
    })

    window?.addEventListener("click", handleClick)

    return () => {
      window?.removeEventListener("click", handleClick)
    }
  }, [])

  const handleSubmit = async () => {
    const obj = {}
    let valid = true
    const regex = new RegExp(passwordRegExp?.regex)
    const emailVal = (user?.sub && user?.sub.split(/[@.]/)) || []
    const errorMsg = []
    if (data?.newPassword?.value?.length < 10) {
      obj.newPassword = {
        ...data?.newPassword,
        error: true,
        mssg: i18n?.passwordError,
      }
      valid = false
      errorMsg.push(i18n?.passwordError)
    }
    emailVal.length > 0 &&
      emailVal.map(val => {
        if (
          data?.newPassword?.value?.toLowerCase().includes(val?.toLowerCase())
        ) {
          obj.newPassword = {
            ...data?.newPassword,
            error: true,
            mssg: i18n?.passwordError,
          }
          valid = false
          errorMsg.push(i18n?.passwordError)
        }
      })
    if (
      data?.oldPassword?.value
        .toLowerCase()
        .includes(data?.newPassword?.value?.toLowerCase())
    ) {
      obj.newPassword = {
        ...data?.newPassword,
        error: true,
        mssg: i18n?.passwordError,
      }
      valid = false
      errorMsg.push(i18n?.passwordError)
    }
    if (!regex.test(data?.oldPassword?.value)) {
      obj.oldPassword = {
        ...data?.oldPassword,
        error: true,
        mssg: i18n?.passwordError,
      }
      valid = false
      errorMsg.push(i18n?.passwordError)
    }
    if (!regex.test(data?.newPassword?.value)) {
      obj.newPassword = {
        ...data?.newPassword,
        error: true,
        mssg: i18n?.passwordError,
      }
      valid = false
      errorMsg.push(i18n?.passwordError)
    } else if (data?.newPassword?.value !== data?.rePassword?.value) {
      obj.rePassword = {
        ...data?.rePassword,
        error: true,
        mssg: i18n?.passwordMatchError,
      }
      valid = false
      errorMsg.push(i18n?.passwordMatchError)
    }
    if (valid) {
      dispatch(
        resetPassword({
          oldPassword: data?.oldPassword?.value,
          newPassword: data?.newPassword?.value,
        })
      )
        .unwrap()
        .then(res => {
          if (res && res?.status === 200) {
            getManagePasswordSubmitAnalytics("success")
            onSuccess()
          } else if (res && res?.status === 403 && res?.data?.errorCauses) {
            if (
              res?.data?.errorCauses[0]?.errorSummary ===
              "Old Password is not correct"
            ) {
              setData({
                ...data,
                oldPassword: {
                  ...data?.oldPassword,
                  error: true,
                  mssg: i18n?.verifyOldPassword,
                },
              })
              errorMsg.push(i18n?.verifyOldPassword)
            } else if (
              res?.data?.errorCauses[0]?.errorSummary ===
              "Password has been used too recently"
            ) {
              setData({
                ...data,
                newPassword: {
                  ...data?.newPassword,
                  error: true,
                  mssg: i18n?.recentPassword,
                },
              })
              errorMsg.push(i18n?.recentPassword)
            } else if (
              res?.data?.errorCauses[0]?.errorSummary ===
              "Password cannot be your current password"
            ) {
              setData({
                ...data,
                newPassword: {
                  ...data?.newPassword,
                  error: true,
                  mssg: i18n?.differentFromOld,
                },
              })
              errorMsg.push(i18n?.differentFromOld)
            }
            getManagePasswordSubmitAnalytics("failure", errorMsg)
          }
        })
    } else {
      const newData = { ...data, ...obj }
      getManagePasswordSubmitAnalytics("failure", errorMsg)
      setData(newData)
    }
  }

  return (
    <div className={`${resetPasswordStyle?.resetPasswordStyleWrapper}`}>
      <div className={`reset-password `}>
        <div className="reset-password__message">{i18n?.requiredFields}</div>
        <Input
          id="kf-reset-old-pwd"
          type="password"
          value={data?.oldPassword?.value}
          maxLength={40}
          placeholder={`${i18n?.enterOldPassword}*`}
          label={`${i18n?.enterOldPassword}*`}
          onChange={e =>
            setData({
              ...data,
              oldPassword: { value: e.target.value, error: false, mssg: "" },
            })
          }
          showError={data?.oldPassword?.error}
          errorMessage={data?.oldPassword?.mssg}
        />
        <Input
          id="kf-reset-new-pwd"
          type="password"
          value={data?.newPassword?.value}
          maxLength={40}
          placeholder={`${i18n?.createPassword}*`}
          label={`${i18n?.createPassword}*`}
          onChange={e =>
            setData({
              ...data,
              newPassword: { value: e.target.value, error: false, mssg: "" },
            })
          }
          showError={data?.newPassword?.error}
          errorMessage={data?.newPassword?.mssg}
        />
        <span
          className="password-requirements gbh-data-layer"
          data-gbh-data-layer='{"eventAction":"my account:password reset:password requirements","eventName":"my account:password reset:password requirements","eventType":"informative","eventMsg":"n/a","eventStatus":"n/a","internalLinkName":"password requirements","internalLinkPosition":"auth modal","internalLinkType":"my account:informative","internalLinkZoneName":"my account:password reset:create new password form","internalLinkURL":"n/a","clickInternalLinks":"true"}'
          name="showModal"
          onClick={() => setShowRequirements(!showRequirements)}
          onBlur={() => setShowRequirements(false)}
          role="tooltip"
          aria-labelledby="reset-password__req-toottip"
          tabIndex="0"
        >
          {sanitizeInnerHtml(i18n?.passwordRequirements)}
        </span>
        <div
          className={`password-requirements__tooltip${
            showRequirements ? "--show" : "--hide"
          }`}
        >
          <div className="password-requirements__tooltip-wrapper">
            <ToolTip
              message={
                <PasswordRequirements
                  passwordRegExp={passwordRegExp}
                  i18n={i18n}
                />
              }
            />
          </div>
        </div>
        <Input
          id="kf-reset-re-pwd"
          type="password"
          value={data?.rePassword?.value}
          maxLength={40}
          placeholder={i18n?.reEnterPassword}
          label={i18n?.reEnterPassword}
          onChange={e =>
            setData({
              ...data,
              rePassword: { value: e.target.value, error: false, mssg: "" },
            })
          }
          showError={data?.rePassword?.error}
          errorMessage={data?.rePassword?.mssg}
        />
        <Button
          type="black"
          role="button"
          flexible
          label={i18n?.submit}
          onClick={() => handleSubmit()}
          useDefaultClass={true}
        />
      </div>
    </div>
  )
}

export default ResetPassword
